import ResourceTable from "@/components/shared/ResourceTable";
import { PageHeader } from "@/components/shared/PageHeader";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const crud = (path) => ({
  fetchList: () => api.get(`/central/${path}`).then((r) => r.data),
  createItem: (b) => api.post(`/central/${path}`, b),
  updateItem: (id, b) => api.put(`/central/${path}/${id}`, b),
  deleteItem: (id) => api.delete(`/central/${path}/${id}`),
});

const STATUS_COLOR = { "À payer": "bg-amber-500/15 text-amber-400", "Payé": "bg-emerald-500/15 text-emerald-400", "Partiellement payé": "bg-blue-500/15 text-blue-400", "Annulé": "bg-red-500/15 text-red-400", "Publié": "bg-emerald-500/15 text-emerald-400", "Brouillon": "bg-slate-500/15 text-slate-400", "Envoyée": "bg-emerald-500/15 text-emerald-400" };
const pill = (s) => <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[s] || "bg-slate-500/15 text-slate-400"}`}>{s}</span>;

export function Campaigns() {
  return (
    <div><PageHeader title="Marketing" subtitle="Campagnes email & WhatsApp vers prospects et clients Vision POS" />
      <ResourceTable testid="central-campaigns" title="Campagnes" createLabel="Nouvelle campagne" {...crud("campaigns")}
        columns={[
          { key: "name", label: "Nom" },
          { key: "channel", label: "Canal" },
          { key: "segment", label: "Segment" },
          { key: "status", label: "Statut", render: (r) => pill(r.status) },
          { key: "created_at", label: "Créée", render: (r) => fmtDate(r.created_at) },
        ]}
        fields={[
          { name: "name", label: "Nom de la campagne", type: "text" },
          { name: "channel", label: "Canal", type: "select", options: ["Email", "WhatsApp", "SMS"], default: "Email" },
          { name: "segment", label: "Segment", type: "text", default: "Tous" },
          { name: "subject", label: "Objet", type: "text" },
          { name: "body", label: "Message", type: "textarea" },
          { name: "status", label: "Statut", type: "select", options: ["Brouillon", "Programmée", "Envoyée"], default: "Brouillon" },
        ]} />
    </div>
  );
}

export function Expenses() {
  return (
    <div><PageHeader title="Charges" subtitle="Charges de fonctionnement de Vision POS" />
      <ResourceTable testid="central-expenses" title="Charges" createLabel="Nouvelle charge" {...crud("expenses")}
        columns={[
          { key: "label", label: "Libellé" },
          { key: "category", label: "Catégorie" },
          { key: "amount", label: "Montant", render: (r) => <span className="mono">{fmtMoney(r.amount)}</span> },
          { key: "supplier", label: "Fournisseur" },
          { key: "status", label: "Statut", render: (r) => pill(r.status) },
        ]}
        fields={[
          { name: "label", label: "Libellé", type: "text" },
          { name: "category", label: "Catégorie", type: "text", default: "Général" },
          { name: "amount", label: "Montant", type: "number" },
          { name: "supplier", label: "Fournisseur", type: "text" },
          { name: "due_date", label: "Échéance", type: "date" },
          { name: "status", label: "Statut", type: "select", options: ["À payer", "Partiellement payé", "Payé", "Annulé"], default: "À payer" },
        ]} />
    </div>
  );
}

export function Treasury() {
  return (
    <div><PageHeader title="Trésorerie" subtitle="Comptes et mouvements de trésorerie Vision POS" />
      <div className="mb-8">
        <ResourceTable testid="central-treasury-accounts" title="Comptes" createLabel="Nouveau compte" {...crud("treasury-accounts")}
          columns={[{ key: "name", label: "Compte" }, { key: "type", label: "Type" }, { key: "balance", label: "Solde", render: (r) => <span className="mono">{fmtMoney(r.balance)}</span> }]}
          fields={[{ name: "name", label: "Nom", type: "text" }, { name: "type", label: "Type", type: "select", options: ["cash", "bank", "mobile"], default: "bank" }, { name: "balance", label: "Solde initial", type: "number" }]} />
      </div>
      <ResourceTable testid="central-treasury-movements" title="Mouvements" createLabel="Nouveau mouvement" {...crud("treasury-movements")}
        columns={[
          { key: "type", label: "Type", render: (r) => <Badge variant="outline" className={r.type === "in" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}>{r.type === "in" ? "Encaissement" : "Décaissement"}</Badge> },
          { key: "amount", label: "Montant", render: (r) => <span className="mono">{fmtMoney(r.amount)}</span> },
          { key: "reason", label: "Motif" }, { key: "method", label: "Mode" }, { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
        ]}
        fields={[
          { name: "type", label: "Type", type: "select", options: ["in", "out"], default: "in" },
          { name: "amount", label: "Montant", type: "number" },
          { name: "reason", label: "Motif", type: "text" },
          { name: "method", label: "Mode de paiement", type: "text", default: "Virement" },
        ]} />
    </div>
  );
}

export function Blog() {
  return (
    <div><PageHeader title="Blog" subtitle="Gérez les articles du site vitrine" />
      <ResourceTable testid="central-blog" title="Articles" createLabel="Nouvel article" {...crud("blog")}
        columns={[
          { key: "title", label: "Titre" }, { key: "category", label: "Catégorie" }, { key: "author", label: "Auteur" },
          { key: "status", label: "Statut", render: (r) => pill(r.status) }, { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
        ]}
        fields={[
          { name: "title", label: "Titre", type: "text" },
          { name: "category", label: "Catégorie", type: "text", default: "Actualités" },
          { name: "author", label: "Auteur", type: "text", default: "Équipe Vision POS" },
          { name: "excerpt", label: "Extrait", type: "textarea" },
          { name: "image", label: "Image (URL)", type: "text" },
          { name: "status", label: "Statut", type: "select", options: ["Brouillon", "Publié"], default: "Brouillon" },
        ]} />
    </div>
  );
}

export function Plans() {
  return (
    <div><PageHeader title="Plans & Tarifs" subtitle="Formules d'abonnement affichées sur le site vitrine" />
      <ResourceTable testid="central-plans" title="Plans" createLabel="Nouveau plan" {...crud("plans")}
        columns={[
          { key: "name", label: "Nom" }, { key: "price", label: "Prix", render: (r) => <span className="mono">{fmtMoney(r.price)}</span> },
          { key: "period", label: "Période" }, { key: "order", label: "Ordre" },
        ]}
        fields={[
          { name: "name", label: "Nom", type: "text" },
          { name: "price", label: "Prix", type: "number" },
          { name: "period", label: "Période", type: "text", default: "mois" },
          { name: "order", label: "Ordre d'affichage", type: "number" },
        ]} />
    </div>
  );
}

export function Audit() {
  return (
    <div><PageHeader title="Journal d'audit" subtitle="Historique des opérations importantes" />
      <ResourceTable testid="central-audit" title="Événements" canCreate={false}
        fetchList={() => api.get("/central/audit").then((r) => r.data)}
        columns={[
          { key: "user", label: "Utilisateur" }, { key: "action", label: "Action" }, { key: "entity", label: "Élément" },
          { key: "detail", label: "Détail" }, { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
        ]} />
    </div>
  );
}
